"use strict";
/**
 * File Name Formatter Utility
 *
 * Provides consistent file name display across client and admin apps.
 * Follows Jeff Dean Google Enterprise principles:
 * - Determinism: Same input → identical output
 * - Idempotency: Can be re-applied safely
 * - Auditability: Raw filename preserved, display name computed
 * - Single Source of Truth: One utility for all apps
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeMonth = normalizeMonth;
exports.normalizeYear = normalizeYear;
exports.parseFileNameMetadata = parseFileNameMetadata;
exports.formatUploadDisplayName = formatUploadDisplayName;
exports.getRunNumberFromDate = getRunNumberFromDate;
exports.extractRctiId = extractRctiId;
exports.extractRctiPaymentDate = extractRctiPaymentDate;
exports.getMonthYearFromRctiDate = getMonthYearFromRctiDate;
// Month abbreviation to full name mapping
const MONTH_MAP = {
    jan: 'January',
    january: 'January',
    feb: 'February',
    february: 'February',
    mar: 'March',
    march: 'March',
    apr: 'April',
    april: 'April',
    may: 'May',
    jun: 'June',
    june: 'June',
    jul: 'July',
    july: 'July',
    aug: 'August',
    august: 'August',
    sep: 'September',
    sept: 'September',
    september: 'September',
    oct: 'October',
    october: 'October',
    nov: 'November',
    november: 'November',
    dec: 'December',
    december: 'December',
};
// Numeric month to full name mapping
const NUMERIC_MONTH_MAP = {
    '01': 'January',
    '02': 'February',
    '03': 'March',
    '04': 'April',
    '05': 'May',
    '06': 'June',
    '07': 'July',
    '08': 'August',
    '09': 'September',
    '10': 'October',
    '11': 'November',
    '12': 'December',
    '1': 'January',
    '2': 'February',
    '3': 'March',
    '4': 'April',
    '5': 'May',
    '6': 'June',
    '7': 'July',
    '8': 'August',
    '9': 'September',
};
/**
 * Normalizes a month value to full month name.
 * Handles: "Nov", "November", "11", "2025-11"
 */
function normalizeMonth(month) {
    if (!month)
        return null;
    // Handle YYYY-MM format (e.g., "2025-11")
    if (month.includes('-')) {
        const parts = month.split('-');
        const monthPart = parts[1];
        return NUMERIC_MONTH_MAP[monthPart] || null;
    }
    // Handle numeric month (e.g., "11" or "11")
    if (/^\d{1,2}$/.test(month)) {
        return NUMERIC_MONTH_MAP[month] || null;
    }
    // Handle month name or abbreviation
    const normalized = MONTH_MAP[month.toLowerCase()];
    return normalized || null;
}
/**
 * Normalizes a year value to 4-digit year.
 * Handles: 2025, 25, "2025", "25"
 */
function normalizeYear(year) {
    if (year === undefined || year === null)
        return null;
    const numYear = typeof year === 'string' ? parseInt(year, 10) : year;
    if (isNaN(numYear))
        return null;
    // 2-digit year (00-99) → assume 2000s
    if (numYear >= 0 && numYear <= 99) {
        return 2000 + numYear;
    }
    // 4-digit year
    if (numYear >= 2000 && numYear <= 2100) {
        return numYear;
    }
    return null;
}
/**
 * Parses filename to extract run number, month, and year.
 * Returns null for fields that cannot be determined.
 *
 * Supported patterns:
 * - "ENTO100 AGMT RUN 1 Oct 25.xlsx" → {run: 1, month: "October", year: 2025}
 * - "RUN 2 - November 2025.xlsx" → {run: 2, month: "November", year: 2025}
 * - "V#PL AGMT RUN 3 Dec 25.xlsx" → {run: 3, month: "December", year: 2025}
 * - "COMM_STMT_12345-MASTER-Nov24.csv" → {run: null, month: "November", year: 2024}
 * - "WLTH-March-2025-EntourageFinancePtyLtd.xlsx" → {run: 1, month: "March", year: 2025}
 * - "BrokerTransaction RCTI ORD-00005296.xls" → {run: 1, month: null, year: null} (ORDE - date from XLS metadata)
 * - "20250616 - Commission Reports - Jun 2025/SomeFile.XLS" → {run: 2, month: "June", year: 2025} (Entors-style folder path)
 */
function parseFileNameMetadata(fileName) {
    // Get extension
    const lastDotIndex = fileName.lastIndexOf('.');
    const extension = lastDotIndex > 0 ? fileName.slice(lastDotIndex + 1) : 'xlsx';
    // Detect MEM (member) file - patterns like "MEM RUN", "_MEM_", " MEM ", "MEM."
    const isMEM = /[_\s]MEM[_\s.]|MEM\s*RUN|^MEM\s/i.test(fileName);
    // ============================================
    // ENTORS/AGGREGATOR FOLDER PATH PATTERN
    // Format: "YYYYMMDD - Commission Reports - Mon YYYY/filename.ext"
    // Example: "20250616 - Commission Reports - Jun 2025/Entors Pty Ltd (Payee)(5148) - Jun 2025.XLS"
    // The date (YYYYMMDD) determines run number: days 1-10→RUN1, 11-20→RUN2, 21-31→RUN3
    // ============================================
    const folderPathMatch = fileName.match(/(\d{4})(\d{2})(\d{2})\s*-\s*Commission Reports\s*-\s*(\w+)\s+(\d{4})/i);
    if (folderPathMatch) {
        const [, yearStr, monthNum, dayStr, monthAbbr, yearFromPath] = folderPathMatch;
        const day = parseInt(dayStr, 10);
        const year = parseInt(yearFromPath, 10);
        const month = MONTH_MAP[monthAbbr.toLowerCase()] ?? null;
        // Determine run number from day of month
        let runNumber;
        if (day >= 1 && day <= 10)
            runNumber = 1;
        else if (day >= 11 && day <= 20)
            runNumber = 2;
        else
            runNumber = 3;
        return {
            runNumber,
            month,
            year: year >= 2000 && year <= 2100 ? year : null,
            extension,
            isMEM: false,
        };
    }
    // ============================================
    // FILENAME WITH MONTH SUFFIX PATTERN (no run number in name)
    // Format: "SomeName - Mon YYYY.ext"
    // Example: "Entors Pty Ltd (Payee)(5148) - Jun 2025.XLS"
    // When no run number is in filename, we need date context to determine run
    // This pattern detects month/year but leaves runNumber null (caller must provide)
    // ============================================
    const monthSuffixMatch = fileName.match(/-\s*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\s+(\d{4})\.[^.]+$/i);
    if (monthSuffixMatch && !fileName.match(/RUN[_\s-]*\d/i)) {
        const [, monthAbbr, yearStr] = monthSuffixMatch;
        const month = MONTH_MAP[monthAbbr.toLowerCase()] ?? null;
        const year = parseInt(yearStr, 10);
        return {
            runNumber: null, // No run number in filename - must come from folder path or stored metadata
            month,
            year: year >= 2000 && year <= 2100 ? year : null,
            extension,
            isMEM,
        };
    }
    // ============================================
    // WLTH PATTERN: WLTH-{Month}-{Year}-{EntityName}.xlsx
    // Example: WLTH-March-2025-EntourageFinancePtyLtd.xlsx
    // WLTH is always single run per month (run = 1)
    // ============================================
    const wlthMatch = fileName.match(/^WLTH-(\w+)-(\d{4})-/i);
    if (wlthMatch) {
        const monthStr = wlthMatch[1];
        const yearStr = wlthMatch[2];
        const month = MONTH_MAP[monthStr.toLowerCase()] ?? null;
        const year = parseInt(yearStr, 10);
        return {
            runNumber: 1, // WLTH is always single run per month
            month,
            year: year >= 2000 && year <= 2100 ? year : null,
            extension,
            isMEM: false,
        };
    }
    // ============================================
    // ORDE PATTERN: BrokerTransaction RCTI ORD-{number}.xls
    // ORDE date is extracted from XLS metadata, not filename
    // ORDE is always single run per month (run = 1)
    // ============================================
    // Linear (no-backtracking) detection — replaces /BrokerTransaction.*RCTI.*ORD-\d+\.xls$/i,
    // which CodeQL flagged as js/polynomial-redos (two unbounded `.*` competing on backtracking).
    // Exactly equivalent: "BrokerTransaction" then "RCTI" (ordered) then a trailing
    // "ORD-<digits>.xls" (the `$` anchor keeps ORD- after both tokens). indexOf is O(n).
    const lowerName = fileName.toLowerCase();
    const btIdx = lowerName.indexOf('brokertransaction');
    const rctiIdx = lowerName.indexOf('rcti', btIdx + 'brokertransaction'.length);
    const ordeMatch = btIdx !== -1 && rctiIdx !== -1 && /ORD-\d+\.xls$/i.test(fileName);
    if (ordeMatch) {
        return {
            runNumber: 1, // ORDE is always single run per month
            month: null, // Date extracted from XLS metadata, not filename
            year: null,
            extension,
            isMEM: false,
        };
    }
    // Extract run number (RUN 1, RUN_2, RUN-3, RUN 1, etc.)
    const runMatch = fileName.match(/RUN[_\s-]*(\d)/i);
    const runNumber = runMatch ? parseInt(runMatch[1], 10) : null;
    // Extract month (full names or abbreviations).
    // Anchored with non-letter boundaries so a month *substring* embedded inside a
    // word is NOT matched — only a standalone month token. Without this, the
    // leftmost-match rule picks "Dec" out of the tenant name "Decru" (and "Mar"
    // from "Marsh", "May" from "Maybank", "Jun" from "Junction", "Aug" from
    // "Augusta"), e.g. "Decru - Peake Run 1 Jun 26.zip" wrongly resolved to
    // December instead of June (prod, 2026-06-15).
    //   - leading `(?:^|[^A-Za-z])`: month must start the string or follow a non-letter
    //   - trailing `(?![A-Za-z])`: month must be followed by a non-letter (digits OK,
    //     so "Nov24" still resolves to November)
    // Deliberately uses a leading non-letter class instead of a lookbehind: lookbehind
    // is unsupported on Safari < 16.4 and this util runs in the browser bundle, where
    // an unsupported regex literal would throw at module-init (white screen).
    const monthPattern = /(?:^|[^A-Za-z])(Jan|January|Feb|February|Mar|March|Apr|April|May|Jun|June|Jul|July|Aug|August|Sep|Sept|September|Oct|October|Nov|November|Dec|December)(?![A-Za-z])/i;
    const monthMatch = fileName.match(monthPattern);
    const month = monthMatch ? (MONTH_MAP[monthMatch[1].toLowerCase()] ?? null) : null;
    // Extract year
    let year = null;
    // Try 4-digit year first (20XX)
    const fullYearMatch = fileName.match(/20(\d{2})/);
    if (fullYearMatch) {
        year = parseInt(`20${fullYearMatch[1]}`, 10);
    }
    else {
        // Try 2-digit year patterns:
        // - "_25" (underscore + digits)
        // - " 25" (space + digits)
        // - "-25" in context like "Nov24" or "-Nov24"
        // - "25." or "25)" at end
        const shortYearMatch = fileName.match(/[\s_-](\d{2})(?:[\s.()]|$)/);
        if (shortYearMatch) {
            const shortYear = parseInt(shortYearMatch[1], 10);
            year = 2000 + shortYear;
        }
        else {
            // Handle patterns like "Nov24" (month immediately followed by 2-digit year)
            const monthYearMatch = fileName.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)(\d{2})/i);
            if (monthYearMatch) {
                year = 2000 + parseInt(monthYearMatch[2], 10);
            }
        }
    }
    return { runNumber, month, year, extension, isMEM };
}
/**
 * Generates a standardized display name for an upload file.
 *
 * Priority order (Jeff Dean: authoritative sources first):
 * 1. Use provided metadata (from Firestore/BigQuery)
 * 2. Parse from filename if metadata incomplete
 * 3. Return original filename if parsing fails (no run number)
 *
 * @param rawFileName - Original filename (e.g., "ENTO100 AGMT RUN 1 Oct 25.xlsx")
 * @param storedMetadata - Optional metadata from database (takes precedence)
 * @returns Formatted display name (e.g., "RUN 1 - October 2025.xlsx") or original filename
 */
function formatUploadDisplayName(rawFileName, storedMetadata) {
    // Parse from filename
    const parsed = parseFileNameMetadata(rawFileName);
    // Merge stored metadata with parsed data (stored takes precedence)
    const runNumber = storedMetadata?.runNumber ?? parsed.runNumber;
    const month = normalizeMonth(storedMetadata?.month) ?? parsed.month;
    const year = normalizeYear(storedMetadata?.year) ?? parsed.year;
    const extension = storedMetadata?.extension ?? parsed.extension;
    // If we don't have a run number, return original filename
    // (Never fabricate run numbers - Jeff Dean principle: no false metadata)
    if (runNumber === null || runNumber === undefined) {
        return rawFileName;
    }
    // If we have run number but missing month or year, still format with what we have
    // but include a fallback marker
    if (!month || !year) {
        // Try to format with partial data, or return original
        if (month && !year) {
            return `RUN ${runNumber} - ${month}.${extension}`;
        }
        if (!month && year) {
            return `RUN ${runNumber} - ${year}.${extension}`;
        }
        // No month and no year, return original
        return rawFileName;
    }
    // Full format: "RUN X - Month YYYY.ext" or "RUN X - Month YYYY - MEM.ext" for MEM files
    const memSuffix = parsed.isMEM ? ' - MEM' : '';
    return `RUN ${runNumber} - ${month} ${year}${memSuffix}.${extension}`;
}
/**
 * Determines run number from a payment date.
 * Days 1-10 → RUN 1, Days 11-20 → RUN 2, Days 21-31 → RUN 3
 *
 * @param date - Date object or string (YYYYMMDD format)
 * @returns Run number (1, 2, or 3)
 */
function getRunNumberFromDate(date) {
    let day;
    if (typeof date === 'string') {
        // Handle YYYYMMDD format (e.g., "20250926")
        if (/^\d{8}$/.test(date)) {
            day = parseInt(date.slice(6, 8), 10);
        }
        else {
            // Try to parse as Date
            const parsed = new Date(date);
            day = parsed.getDate();
        }
    }
    else {
        day = date.getDate();
    }
    if (day >= 1 && day <= 10)
        return 1;
    if (day >= 11 && day <= 20)
        return 2;
    return 3;
}
/**
 * Extracts the RCTI ID from a filename.
 * Used to match RCTI PDFs with their corresponding CSV files.
 *
 * Patterns:
 * - "RCTI-ENTOURAGE CRANBOURNE PTY LTD-20250926-1061435.pdf" → "1061435"
 * - "COMM_STMT_ENTOURAGE CRANBOURNE PTY LTD-MASTER-1061435.csv" → "1061435"
 *
 * @param fileName - The filename to extract ID from
 * @returns The RCTI ID or null if not found
 */
function extractRctiId(fileName) {
    // Pattern for RCTI PDF: ends with -{id}.pdf
    const rctiMatch = fileName.match(/-(\d{6,8})\.pdf$/i);
    if (rctiMatch)
        return rctiMatch[1];
    // Pattern for COMM_STMT CSV: ends with -MASTER-{id}.csv
    const csvMatch = fileName.match(/-MASTER-(\d{6,8})\.csv$/i);
    if (csvMatch)
        return csvMatch[1];
    return null;
}
/**
 * Extracts payment date from an RCTI filename.
 *
 * Pattern: "RCTI-*-YYYYMMDD-{id}.pdf"
 * Example: "RCTI-ENTOURAGE CRANBOURNE PTY LTD-20250926-1061435.pdf" → "20250926"
 *
 * @param rctiFileName - The RCTI PDF filename
 * @returns Payment date in YYYYMMDD format or null if not found
 */
function extractRctiPaymentDate(rctiFileName) {
    // Pattern: -YYYYMMDD- in the filename
    const match = rctiFileName.match(/-(\d{8})-\d+\.pdf$/i);
    return match ? match[1] : null;
}
/**
 * Extracts month and year from an RCTI payment date.
 *
 * @param paymentDate - Payment date in YYYYMMDD format (e.g., "20250926")
 * @returns Object with month name and year, or null values if parsing fails
 */
function getMonthYearFromRctiDate(paymentDate) {
    if (!/^\d{8}$/.test(paymentDate)) {
        return { month: null, year: null };
    }
    const yearStr = paymentDate.slice(0, 4);
    const monthStr = paymentDate.slice(4, 6);
    const year = parseInt(yearStr, 10);
    const month = NUMERIC_MONTH_MAP[monthStr] || null;
    return { month, year };
}
